

// REPO PATH:  src/front-end/code/app/socketService/events.js
//
// FIX: live "meta/updated" broadcast now applies via updateMeta (single-field
// delta), not setAllMeta (full map). setAllMeta remains for the initial /all
// load in processMeta. Device renames now sync correctly to all clients.


import store from '../../slices/store';
import { updateData, updateLocations, updateSeriesKeyMap } from '../../slices/data';
import { normalizeChartName } from '../../slices/data/chartName';
import { setBothRules } from '../../slices/rules/actions';
import { setBothControls } from '../../slices/controls/actions';
import { setColors, addColors } from '../../slices/colors';
import { allocateColors } from '../../slices/colors/allocate';
import { setAliases } from '../../slices/aliases';
import { setOta, setDeviceProgress, clearDeviceProgress } from '../../slices/otas';
import { updateDevices, setAllMeta, updateMeta } from '../../slices/devices';
import { mergeBothSettings } from '../../slices/settings/actions';
import { setMessages, addMessage } from '../../slices/messages';
import { pulseDevice } from './pulseBus';
import { recordServerTimestamp } from './clockOffset';
import hooks from '../../actions/hooks';


const updateMap = {
    'rules/updated':    setBothRules,
    'controls/updated': setBothControls,
    // PARTIAL payload: the settings service broadcasts the tenant settings doc
    // only -- preferences (firstName, alert email, consent flags) are a separate
    // collection and are absent. setBothSettings replaces the store wholesale,
    // which dropped them; mergeBothSettings layers the delta onto current data.
    'settings/updated': mergeBothSettings,
    'messages/added':   addMessage,
    // The live meta broadcast ships a SINGLE-field delta { deviceID, field, value }
    // (one rename), so it must use the single-field reducer. setAllMeta expects a
    // full deviceID->meta map and is only correct for the initial /all load
    // (see processMeta below); feeding it a delta corrupts the device list on
    // every OTHER logged-in client.
    'meta/updated':     updateMeta,
    'colors/updated':   setColors,
    'aliases/updated':  setAliases,
    'ota/updated':      setOta,
};

export const processUpdate = function(packet, dispatch) {
    const { target, payload } = packet;
    if (updateMap[target]) dispatch(updateMap[target](payload));
};


export const processTwin = function(packet, dispatch) {
    dispatch(updateDevices(packet));
    const { timestamp, values } = packet || {};
    if (values) {
        for (const deviceID in values) {
            pulseDevice(deviceID, timestamp || Date.now());
        }
    }
};

export const processData = function(packet, dispatch, getAccessTokenSilently) {
	// console.log('[processData] fired', packet?.timestamp, Object.keys(packet?.values || {}));
    const { target, timestamp, values } = packet;

    // Pair this arrival instant with the packet's server timestamp so the
    // graph's live edge can be drawn on server time instead of this device's
    // (possibly skewed) local clock. See clockOffset.js.
    recordServerTimestamp(timestamp);

    const currentState = store.getState();
    const existingColors = currentState.colors.allocated;
    const seriesKeyMap = currentState.data.seriesKeyMap;
    const keyMapUpdates = {};

    // orderedKeyMap covers everything allocateColors needs: every already-
    // colored series (so it can reconstruct each device's base hue and how
    // many shades are already in use) plus whatever's new in this packet,
    // in first-seen order.
    const orderedKeyMap = {};
    for (const seriesKey in existingColors) {
        const mapping = seriesKeyMap[seriesKey];
        if (mapping) orderedKeyMap[seriesKey] = mapping;
    }

    for (const deviceID in values) {
        pulseDevice(deviceID, timestamp || Date.now());

        for (const type in values[deviceID]) {
            for (const rawChart in values[deviceID][type]) {
                const fields = values[deviceID][type][rawChart];
                // Live packets arrive normalized from the processor; condensed
                // table updates rebuilt from OLD db rows may not. Idempotent.
                const chartName = normalizeChartName(rawChart);

                for (const fieldName in fields) {
                    const seriesKey = deviceID + '_' + fieldName;
                    if (!seriesKeyMap[seriesKey]) keyMapUpdates[seriesKey] = { deviceID, fieldName };
                    if (!orderedKeyMap[seriesKey]) orderedKeyMap[seriesKey] = { deviceID, fieldName };
                    dispatch(updateData({ target, chartName, seriesKey, time: timestamp, data: fields[fieldName] }));
                }
            }
        }
    }

    // One base hue per device, reused across every field/chart it has; a
    // 2nd/3rd field on the same chart gets a darker shade of that same hue
    // instead of an unrelated color. Manual picks are left untouched.
    const newColorEntries = allocateColors(orderedKeyMap, existingColors);
    const hasNewColors = Object.keys(newColorEntries).length > 0;
    if (hasNewColors) {
        dispatch(addColors(newColorEntries));
    }

    if (Object.keys(keyMapUpdates).length) {
        dispatch(updateSeriesKeyMap(keyMapUpdates));
    }

    if (hasNewColors) {
        getAccessTokenSilently().then(token => {
            const newColors = store.getState().colors.allocated;
            hooks.put('/update/colors', newColors, token);
        });
    }
};

// OwnTracks location delta: { timestamp, values: { chartName: { person: point } } }.
// A person's dot is one series, keyed `${person}_loc` — person fills the
// deviceID slot of the house `deviceID_fieldName` convention, so the alias
// resolver, color allocator, and legend all treat map series like any other.
// The legend's color picker therefore doubles as the dot-color picker.
export const processMap = function(packet, dispatch, getAccessTokenSilently) {
    const rawValues = (packet || {}).values;
    if (!rawValues) return;

    // Fold any pre-normalization chart keys into canonical form. New name
    // (not a reassignment of the destructured const — that threw
    // '"values" is read-only' at runtime).
    const values = {};
    for (const rawChart in rawValues) {
        const chartName = normalizeChartName(rawChart);
        values[chartName] = Object.assign(values[chartName] || {}, rawValues[rawChart]);
    }

    dispatch(updateLocations(values));

    const currentState = store.getState();
    const existingColors = currentState.colors.allocated;
    const seriesKeyMap = currentState.data.seriesKeyMap;
    const keyMapUpdates = {};

    const orderedKeyMap = {};
    for (const seriesKey in existingColors) {
        const mapping = seriesKeyMap[seriesKey];
        if (mapping) orderedKeyMap[seriesKey] = mapping;
    }

    for (const chartName in values) {
        for (const person in values[chartName]) {
            const seriesKey = person + '_loc';
            keyMapUpdates[seriesKey] = { deviceID: person, fieldName: 'loc' };
            if (!orderedKeyMap[seriesKey]) orderedKeyMap[seriesKey] = { deviceID: person, fieldName: 'loc' };
        }
    }

    const newColorEntries = allocateColors(orderedKeyMap, existingColors);
    const hasNewColors = Object.keys(newColorEntries).length > 0;
    if (hasNewColors) dispatch(addColors(newColorEntries));

    if (Object.keys(keyMapUpdates).length) dispatch(updateSeriesKeyMap(keyMapUpdates));

    if (hasNewColors) {
        getAccessTokenSilently().then(token => {
            const newColors = store.getState().colors.allocated;
            hooks.put('/update/colors', newColors, token);
        });
    }
};

export const processMeta = function(packet, dispatch) {
    dispatch(setAllMeta(packet));
};

export const processMessages = function(messages, dispatch) {
    dispatch(setMessages(messages));
};


export const processOta = function(packet, dispatch) {
    const { deviceID, tag, payload } = packet || {};
    if (!deviceID || !tag) return;

    // Only 'progress' drives the transient live bar. Once the download
    // finishes (complete) or any lifecycle signal arrives (accepted /
    // rolled_back / failed), clear the live entry so the row falls through
    // to the PERSISTED device.status — which settings has set to pending /
    // accepted / rolled_back. Otherwise a stale 'success' live badge would
    // mask the real status forever.
    if (tag === 'progress') {
        dispatch(setDeviceProgress({
            deviceID,
            entry: { pct: Number(payload) || 0, phase: 'in_progress' }
        }));
    } else {
        // complete | accepted | rolled_back | failed → live bar is done
        dispatch(clearDeviceProgress({ deviceID }));
    }
};